import { AiAssistantConfig, RealtimeSessionResponse } from './ai-assistant.types';

export class ConnectionManager {
	private peerConnection: RTCPeerConnection | null = null;
	private dataChannel: RTCDataChannel | null = null;
	private audioElement: HTMLAudioElement | null = null;

	constructor(private readonly config: AiAssistantConfig) {}

	/**
	 * Initialize WebRTC connection components
	 */
	public async initialize(): Promise<void> {
		await this.setupAudioElement();
		await this.setupPeerConnection();
		await this.setupMediaStream();
		this.createDataChannel();
	}

	/**
	 * Establish the WebRTC connection with OpenAI
	 */
	public async establishConnection(sessionData: RealtimeSessionResponse): Promise<void> {
		if (!this.peerConnection) {
			throw new Error('Peer connection not initialized');
		}

		const offer = await this.peerConnection.createOffer();
		await this.peerConnection.setLocalDescription(offer);

		const sdpResponse = await fetch(`${this.config.baseUrl}?model=${sessionData.model}`, {
			method: 'POST',
			body: offer.sdp,
			headers: {
				Authorization: `Bearer ${sessionData.client_secret.value}`,
				'Content-Type': 'application/sdp',
			},
		});

		if (!sdpResponse.ok) {
			throw new Error(`SDP request failed: ${sdpResponse.status} ${sdpResponse.statusText}`);
		}

		const answer = {
			type: 'answer',
			sdp: await sdpResponse.text(),
		} as RTCSessionDescriptionInit;

		await this.peerConnection.setRemoteDescription(answer);
	}

	/**
	 * Get the data channel for communication
	 */
	public getDataChannel(): RTCDataChannel | null {
		return this.dataChannel;
	}

	/**
	 * Clean up all connection resources
	 */
	public cleanup(): void {
		this.cleanupAudioElement();
		this.cleanupDataChannel();
		this.cleanupPeerConnection();
	}

	/**
	 * Setup audio element for playback
	 */
	private async setupAudioElement(): Promise<void> {
		this.audioElement = document.createElement('audio');
		this.audioElement.autoplay = true;
		document.body.appendChild(this.audioElement);
	}

	/**
	 * Setup peer connection with track handling
	 */
	private async setupPeerConnection(): Promise<void> {
		this.peerConnection = new RTCPeerConnection();

		this.peerConnection.ontrack = (event: RTCTrackEvent): void => {
			if (this.audioElement && event.streams[0]) {
				this.audioElement.srcObject = event.streams[0];
			}
		};

		this.peerConnection.onconnectionstatechange = (): void => {
			// Connection state changes are tracked internally
		};

		this.peerConnection.onicecandidate = (_event): void => {
			// ICE candidates are handled automatically by WebRTC
		};
	}

	/**
	 * Setup media stream (microphone)
	 */
	private async setupMediaStream(): Promise<void> {
		if (!this.peerConnection) {
			throw new Error('Peer connection not initialized');
		}

		try {
			const mediaStream = await navigator.mediaDevices.getUserMedia({
				audio: {
					echoCancellation: true,
					noiseSuppression: true,
					autoGainControl: true,
				},
			});

			const audioTrack = mediaStream.getTracks()[0];
			if (audioTrack) {
				this.peerConnection.addTrack(audioTrack, mediaStream);
			}
		} catch (error) {
			console.error('Failed to get user media:', error);
			throw new Error('Failed to access microphone. Please check permissions.');
		}
	}

	/**
	 * Create data channel for bi-directional communication
	 */
	private createDataChannel(): void {
		if (!this.peerConnection) {
			throw new Error('Peer connection not initialized');
		}

		this.dataChannel = this.peerConnection.createDataChannel('oai-events', {
			ordered: true,
		});

		this.dataChannel.onopen = (): void => {
			// Data channel is now ready for communication
		};

		this.dataChannel.onclose = (): void => {
			// Data channel has been closed
		};

		this.dataChannel.onerror = (error): void => {
			console.error('Data channel error:', error);
		};
	}

	/**
	 * Clean up audio element
	 */
	private cleanupAudioElement(): void {
		if (this.audioElement) {
			this.audioElement.pause();
			this.audioElement.srcObject = null;
			this.audioElement.remove();
			this.audioElement = null;
		}
	}

	/**
	 * Clean up data channel
	 */
	private cleanupDataChannel(): void {
		if (this.dataChannel) {
			this.dataChannel.close();
			this.dataChannel = null;
		}
	}

	/**
	 * Clean up peer connection
	 */
	private cleanupPeerConnection(): void {
		if (this.peerConnection) {
			// Stop all tracks
			this.peerConnection.getSenders().forEach(sender => {
				if (sender.track) {
					sender.track.stop();
				}
			});

			this.peerConnection.close();
			this.peerConnection = null;
		}
	}
}
